import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import type {
  ReceiptImageAnalysis,
  BudgetResponse,
  CreateBudgetParams,
  CreateExpenseExportParams,
  DeleteBudgetParams,
  GetBudgetsParams,
  GetBudgetsResponse,
  UpdateBudgetParams,
  CreateExpenseExportResponse,
  CreateExpenseParams,
  CreateExpenseResponse,
  DeleteExpenseParams,
  GetExpenseResponse,
  GetExpenseSubcategoriesResponse,
  GetExpenseTaxesResponse,
  GetExpensesResponse,
  GetExpensesParams,
  IExpensePhoenixApi,
  ScheduleExpensesForExportParams,
  ScheduleExpensesForExportResponse,
  UpdateExpenseParams,
  UpdateExpenseResponse,
} from './api';
import { ExpensePhoenixApi, pollExpenseExportZip } from './api';
import {
  BUDGETS_PHOENIX_KEY,
  BUDGETS_PHOENIX_MAIN_KEY,
  EXPENSE_BY_ID_KEY,
  EXPENSES_BY_CASH_GROUP_KEY,
  EXPENSE_SUBCATEGORIES_PHOENIX_KEY,
  EXPENSE_TAXES_PHOENIX_KEY,
  EXPENSES_PHOENIX_KEY,
  EXPENSES_PHOENIX_MAIN_KEY,
} from './queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import {
  CASH_LIST_TRANSACTIONS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import { CASH_TRANSACTION_GROUP_LIST_KEY } from '../cash-management/queryKeys';

export type {
  AnalyzeReceiptImageParams,
  ReceiptImageAnalysis,
  Budget,
  BudgetAlert,
  BudgetAlertCountLevel,
  BudgetAlertInput,
  BudgetAlertSeverity,
  BudgetConsumption,
  BudgetInterval,
  BudgetResponse,
  BudgetSortColumn,
  CreateBudgetBody,
  CreateBudgetParams,
  DeleteBudgetParams,
  GetBudgetsParams,
  GetBudgetsResponse,
  UpdateBudgetBody,
  UpdateBudgetParams,
  CreateExpenseBody,
  CreateExpenseExportParams,
  CreateExpenseExportResponse,
  CreateExpenseParams,
  CreateExpenseResponse,
  DeleteExpenseParams,
  Expense,
  ExpenseCategory,
  ExpenseCashTransactionGroup,
  ExpenseCsvExportRow,
  ExpenseExportSummary,
  ExpenseFilters,
  ExpenseListMeta,
  ExpensePart,
  ExpenseSubcategory,
  ExpenseSubcategorySummary,
  ExpenseExportZipStatus,
  ExpenseTab,
  ExpenseTax,
  GetExpenseTaxesResponse,
  GetExpenseCsvExportParams,
  GetExpenseCsvExportResponse,
  GetExpenseSubcategoriesResponse,
  GetExpensesParams,
  GetExpensesResponse,
  IExpensePhoenixApi,
  ScheduleExpensesForExportParams,
  ScheduleExpensesForExportResponse,
  UpdateExpenseBody,
  UpdateExpenseParams,
  UpdateExpensePart,
  UpdateExpenseResponse,
} from './api';
export {
  BUDGET_ALERT_COUNT_LEVELS,
  BUDGET_ALERT_SEVERITIES,
  BUDGET_INTERVALS,
  BUDGET_SORT_COLUMNS,
  EXPENSE_CATEGORIES,
  formatExpenseTaxLabel,
} from './api';

export type { PartDraft } from './expensePartsDrafts';
export {
  createInternalId,
  draftsToUpdateParts,
  expensePartsToDrafts,
  partsSumMatches,
  sumPartAmounts,
} from './expensePartsDrafts';

const ApiContext = createContext<IExpensePhoenixApi | undefined>(undefined);
const { useApi: useExpensePhoenixApi, ApiProvider: ExpensePhoenixApiProvider } =
  makeApiProvider({
    name: 'ExpensePhoenix',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new ExpensePhoenixApi(tenantId, auth, apiUrl),
  });

export { ExpensePhoenixApiProvider, useExpensePhoenixApi };

export function useAnalyzeReceiptImage(): (
  imageId: string
) => Promise<ReceiptImageAnalysis> {
  const api = useExpensePhoenixApi();
  return useCallback(
    (imageId: string) =>
      api.analyzeReceiptImage({ params: { imageId }, body: undefined }),
    [api]
  );
}

export function useExpenses(
  params: GetExpensesParams['params'],
  options?: Omit<
    UseQueryOptions<GetExpensesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpensesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSES_PHOENIX_KEY(params),
    (api) => {
      return api.getExpenses({ params });
    },
    {
      ...options,
      placeholderData: keepPreviousData,
    }
  );
}

export function useCreateExpense(): UseMutationResult<
  CreateExpenseResponse,
  unknown,
  CreateExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<CreateExpenseResponse, unknown, CreateExpenseParams>({
    mutationKey: ['expense-create'],
    mutationFn: (data) => api.createExpense(data),
    onSuccess: async (_response, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            variables.body.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useUpdateExpense(): UseMutationResult<
  UpdateExpenseResponse,
  unknown,
  UpdateExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<UpdateExpenseResponse, unknown, UpdateExpenseParams>({
    mutationKey: ['expense-update'],
    mutationFn: (data) => api.updateExpense(data),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            response.data.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useScheduleExpensesForExport(): UseMutationResult<
  ScheduleExpensesForExportResponse,
  unknown,
  ScheduleExpensesForExportParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    ScheduleExpensesForExportResponse,
    unknown,
    ScheduleExpensesForExportParams
  >({
    mutationKey: ['expense-schedule-for-export'],
    mutationFn: (data) => api.scheduleExpensesForExport(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useCreateExpenseExport(): UseMutationResult<
  CreateExpenseExportResponse,
  unknown,
  CreateExpenseExportParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    CreateExpenseExportResponse,
    unknown,
    CreateExpenseExportParams
  >({
    mutationKey: ['expense-create-export'],
    mutationFn: (data) => api.createExpenseExport(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

/**
 * Either starts a ZIP export for the given expenses (like `useCreateExpenseExport`,
 * but producing a ZIP with the receipt images) or rebuilds the ZIP of an export
 * that already exists.
 */
export type DownloadExpenseExportZipParams =
  | { expense_ids: string[] }
  | { expense_export_id: string };

/**
 * Builds the export ZIP on the backend and downloads it once it is ready.
 *
 * Unlike the CSV export the backend answers with `202 Accepted` and builds the
 * ZIP asynchronously, so the mutation stays pending while it polls the status
 * endpoint and only settles when the file can be downloaded.
 */
export function useDownloadExpenseExportZip(): UseMutationResult<
  string,
  unknown,
  DownloadExpenseExportZipParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<string, unknown, DownloadExpenseExportZipParams>({
    mutationKey: ['expense-download-export-zip'],
    mutationFn: async (data) => {
      const { data: started } =
        'expense_ids' in data
          ? await api.createExpenseExportZip({
              body: { expense_ids: data.expense_ids },
            })
          : await api.rebuildExpenseExportZip({
              params: { expenseExportId: data.expense_export_id },
              body: undefined,
            });

      return pollExpenseExportZip({
        getStatus: () =>
          api.getExpenseExportZipStatus({
            params: { expenseExportId: started.export_id },
          }),
      });
    },
    onSuccess: async (downloadUrl) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
      // The presigned URL serves `Content-Type: application/zip`, so the browser
      // downloads it instead of navigating away from the page.
      window.location.href = downloadUrl;
    },
  });
}

export function useUnscheduleExpensesForExport(): UseMutationResult<
  ScheduleExpensesForExportResponse,
  unknown,
  ScheduleExpensesForExportParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    ScheduleExpensesForExportResponse,
    unknown,
    ScheduleExpensesForExportParams
  >({
    mutationKey: ['expense-unschedule-for-export'],
    mutationFn: (data) => api.unscheduleExpensesForExport(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useDeleteExpense(): UseMutationResult<
  void,
  unknown,
  DeleteExpenseParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteExpenseParams>({
    mutationKey: ['expense-delete'],
    mutationFn: (data) => api.deleteExpense(data),
    onSuccess: async (_response, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXPENSES_PHOENIX_MAIN_KEY }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            variables.params.cash_list_transaction_group_id
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
  });
}

export function useBudgets(
  params: GetBudgetsParams['params'],
  options?: Omit<
    UseQueryOptions<GetBudgetsResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetBudgetsResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    BUDGETS_PHOENIX_KEY(params),
    (api) => api.getBudgets({ params }),
    {
      ...options,
      placeholderData: keepPreviousData,
    }
  );
}

export function useCreateBudget(): UseMutationResult<
  BudgetResponse,
  unknown,
  CreateBudgetParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<BudgetResponse, unknown, CreateBudgetParams>({
    mutationKey: ['budget-create'],
    mutationFn: (data) => api.createBudget(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: BUDGETS_PHOENIX_MAIN_KEY,
      });
    },
  });
}

export function useUpdateBudget(): UseMutationResult<
  BudgetResponse,
  unknown,
  UpdateBudgetParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<BudgetResponse, unknown, UpdateBudgetParams>({
    mutationKey: ['budget-update'],
    mutationFn: (data) => api.updateBudget(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: BUDGETS_PHOENIX_MAIN_KEY,
      });
    },
  });
}

export function useDeleteBudget(): UseMutationResult<
  void,
  unknown,
  DeleteBudgetParams
> {
  const api = useExpensePhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteBudgetParams>({
    mutationKey: ['budget-delete'],
    mutationFn: (data) => api.deleteBudget(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: BUDGETS_PHOENIX_MAIN_KEY,
      });
    },
  });
}

export function useExpenseSubcategories(
  options?: Omit<
    UseQueryOptions<GetExpenseSubcategoriesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpenseSubcategoriesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSE_SUBCATEGORIES_PHOENIX_KEY,
    (api) => api.getExpenseSubcategories(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
    }
  );
}

export function useExpenseTaxes(
  options?: Omit<
    UseQueryOptions<GetExpenseTaxesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpenseTaxesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSE_TAXES_PHOENIX_KEY,
    (api) => api.getExpenseTaxes(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
    }
  );
}

export function useExpenseById(
  expenseId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetExpenseResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpenseResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSE_BY_ID_KEY(expenseId),
    (api) => {
      if (!expenseId) {
        throw new Error('expenseId is required');
      }
      return api.getExpense({ params: { expense_id: expenseId } });
    },
    { ...options, enabled: Boolean(expenseId) && options?.enabled !== false }
  );
}

export function useExpensesByCashGroup(
  cashTransactionGroupId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetExpensesResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetExpensesResponse, unknown> {
  return useApiQuery(
    useExpensePhoenixApi,
    EXPENSES_BY_CASH_GROUP_KEY(cashTransactionGroupId),
    (api) => {
      if (!cashTransactionGroupId) {
        throw new Error('cashTransactionGroupId is required');
      }
      return api.getExpensesByCashGroup({
        params: {
          cash_list_transaction_group_id: cashTransactionGroupId,
        },
      });
    },
    {
      ...options,
      enabled: Boolean(cashTransactionGroupId) && options?.enabled !== false,
    }
  );
}
